/**
 * Web Audio synthesized SFX. Mirrors the Android `SoundController`, which
 * synthesizes tiny tonal blips on the fly so we don't need to ship audio
 * assets.
 *
 * Each effect is a short sine sweep with a quick exponential decay envelope.
 * The AudioContext is created lazily on the first user gesture (browsers
 * require this).
 */
const SAMPLE_RATE = 22050;

let ctx: AudioContext | null = null;
let enabled = true;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  ctx = new Ctor();
  return ctx;
}

export function setEnabled(value: boolean): void {
  enabled = value;
}

export function unlockAudio(): void {
  const c = getCtx();
  if (c && c.state === 'suspended') void c.resume();
}

function play(
  frequencyHz: number,
  durationMs: number,
  sweepTo: number,
  volume: number,
): void {
  if (!enabled) return;
  const c = getCtx();
  if (!c) return;

  const totalSamples = Math.floor((SAMPLE_RATE * durationMs) / 1000);
  const buffer = c.createBuffer(1, totalSamples, SAMPLE_RATE);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < totalSamples; i++) {
    const progress = i / totalSamples;
    const freq = frequencyHz + (sweepTo - frequencyHz) * progress;
    const t = i / SAMPLE_RATE;
    const envelope = Math.exp(-3.5 * progress);
    const sample = Math.sin(2 * Math.PI * freq * t) * envelope * volume;
    data[i] = sample;
  }

  const src = c.createBufferSource();
  src.buffer = buffer;
  const gain = c.createGain();
  gain.gain.value = 1;
  src.connect(gain).connect(c.destination);
  src.start();
}

export function playEat(): void {
  play(880, 90, 1320, 0.55);
}
export function playLevelUp(): void {
  play(440, 250, 1760, 0.55);
}
export function playTurn(): void {
  play(440, 40, 440, 0.25);
}
export function playBoost(): void {
  play(660, 120, 880, 0.4);
}
export function playDash(): void {
  play(220, 80, 110, 0.5);
}
export function playGameOver(): void {
  play(440, 700, 110, 0.6);
}
